import { ReadonlyMat4 } from 'gl-matrix';
import {
    Transformable,
    Transformation,
} from '../../util/geometry/Transformation';
import { Grid } from './Grid';
import { RotatedBoundingBox } from './RotatedBoundingBox';
import { VertexArrayObject } from '~/engine/core/utils/VertexArrayObject';

/**
 * {@link EnvironmentObject EnvironmentObjects} are objects that may be stored within {@link Environment}.\
 * These objects have some visual presentation in the 3D PatientSpace.\
 * Most of these allocate some WebGL related data.\
 * They may also be given some {@link Transformation} to transform them.\
 * Examples of {@link EnvironmentObject EnvironmentObjects} would be Images, StructureSets and Doses.
 */
export abstract class EnvironmentObject implements Transformable {
    //TODO: EnvironmentObjects may have transformations

    protected _transformation?: Transformation;

    protected _grid?: Grid;

    protected constructor() {}

    /**
     * Method to get the optimal {@link RotatedBoundingBox}.\
     * This {@link RotatedBoundingBox} covers all the data present within this {@link EnvironmentObject}.\
     * It has a rotation.\
     * And {@link BoundingBox} that covers all the data.
     */
    public abstract get boundingBox(): RotatedBoundingBox;

    public abstract delete(): void;

    // TODO: move elsewhere. EnvironmentObject should not be responsible for the additional transformation, it is not a property of the object itself- it is a property of the view.
    get transformation(): Transformation | undefined {
        return this._transformation;
    }

    set transformation(transformation: Transformation | undefined) {
        this._transformation = transformation;
    }

    get transformationMatrix(): ReadonlyMat4 | undefined {
        return this._transformation
            ? this._transformation.totalTransformation
            : undefined;
    }

    /**
     * Method to calculate the intersection between this bounding box and a projection plane.\
     */
    public crossSection(
        toProjection: ReadonlyMat4,
        transformation?: ReadonlyMat4
    ): VertexArrayObject | undefined {
        return this.boundingBox.crossSection(toProjection, transformation);
    }
}

export type EnvironmentObjectClass<T extends EnvironmentObject> = new (
    ...args: never[]
) => T;
